import { EventSourcePolyfill } from 'event-source-polyfill';

import { CLIENT_TIMEOUT_DEFAULTS, getClientTimeout } from './clientTimeoutConfig';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Compiled-in cap on one reconnect wait. The admin timeout panel can change it
 * (`client.sse_max_backoff_ms`); reconnectDelayMs reads the configured value.
 */
export const MAX_RECONNECT_BACKOFF_MS = CLIENT_TIMEOUT_DEFAULTS.sseMaxBackoffMs;

/** Browsers and Node run a timer longer than 2^31-1 ms after about 1 ms, which would retry in a tight loop. */
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

/**
 * Reconnect `attempt` (1-based) waits base x 2^(attempt-1), capped at the larger of the
 * base and the configured backoff cap, so a high retry limit never overflows the timer.
 * The backoff stops doubling at the cap; a base above it still waits its own value.
 */
export const reconnectDelayMs = (attempt: number): number => {
  const base = getClientTimeout('sseBaseDelayMs');
  const cap = Math.min(Math.max(base, getClientTimeout('sseMaxBackoffMs')), MAX_TIMER_DELAY_MS);
  const exponent = Math.min(Math.max(attempt - 1, 0), 31);

  return Math.min(base * 2 ** exponent, cap);
};

type EventType = 'reward_offered' | 'reward_updated' | 'project_title_updated' | 'tasks_update' | 'task_update' | 'task_plan_update' | 'requirements' | 'chat_message' | 'chat_message_stream_start' | 'chat_message_stream_delta' | 'chat_message_stream_done' | 'agent_log' | 'task_log' | 'task_log_batch' | 'task_log_closed' | 'heartbeat' | 'connected' | 'architecture' | 'user_flows' | 'design_pages' | 'build_update' | 'credits_update' | 'planning_complete' | 'agent_state_restore' | 'design_review_needed' | 'ai_overview_text' | 'ai_task_generation' | 'planning_stage_loader' | 'srd_stream_chunk' | 'srd_proposal_thinking' | 'srd_proposal_ready' | 'srd_proposal_rejected' | 'srd_proposal_failed' | 'architecture_generation_started' | 'architecture_proposal_thinking' | 'architecture_proposal_ready' | 'architecture_proposal_rejected' | 'architecture_proposal_failed' | 'user_flow_proposal_thinking' | 'user_flow_proposal_ready' | 'user_flow_proposal_rejected' | 'user_flow_proposal_failed' | 'insufficient_credits' | 'pm_inline_status' | 'code_ready' | 'vcs_status' | 'chat_session_updated' | 'developer_mode_status' | 'developer_mode_queued_message' | 'developer_mode_queued_message_update' | 'developer_mode_local_command_request' | 'developer_mode_local_command_update' | 'developer_mode_local_command_result' | 'doc_processing' | 'suggested_agents' | 'doc_upload_choice' | 'project_reset' | 'sprints_update' | 'build_phases_update' | 'task_discussion_update' | 'gantt_schedule_update';

type EventCallback = (data: unknown) => void;

class EventService {
  private eventSource: EventSourcePolyfill | null = null;

  private listeners: Map<EventType, Set<EventCallback>> = new Map();

  private currentProjectId: string | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private retryCount = 0;

  constructor() {
    this.handleMessage = this.handleMessage.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    window.addEventListener('online', this.handleOnline);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Connect to the unified event stream for a project.
   * If already connected to the same project, does nothing.
   * If connected to a different project, disconnects first.
   */
  connect(projectId: string): void {
    // Check if we are already connected OR connecting to this project
    // 0 = CONNECTING, 1 = OPEN
    if (
      this.currentProjectId === projectId &&
      this.eventSource &&
      (this.eventSource.readyState === 1 || this.eventSource.readyState === 0)
    ) {
      return; // Already connected or connecting
    }

    if (this.currentProjectId && this.currentProjectId !== projectId) {
      this.disconnect();
    }

    this.currentProjectId = projectId;
    this.retryCount = 0;
    this.establishConnection(projectId);
  }

  private establishConnection(projectId: string): void {
    const token = tokenService.getAccessToken();

    if (!token) {
      return;
    }

    this.createEventSource(projectId, token);
  }

  private createEventSource(projectId: string, token: string): void {
    if (this.eventSource) {
      this.eventSource.close();
    }

    const options = {
      headers: { Authorization: `Bearer ${token}` },
      heartbeatTimeout: getClientTimeout('sseHeartbeatTimeoutMs'),
    };

    this.eventSource = new EventSourcePolyfill(
      `${API_URL}/api/v1/projects/${projectId}/events`,
      options as unknown as Record<string, unknown>
    ) as EventSourcePolyfill;

    this.setupEventHandlers(projectId);
  }

  private setupEventHandlers(projectId: string): void {
    if (!this.eventSource) return;

    this.eventSource.onopen = () => { this.retryCount = 0; };
    this.eventSource.onmessage = this.handleMessage;
    this.eventSource.onerror = (e: Event) => this.handleError(e, projectId);
  }

  private closeSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  disconnect(): void {
    this.closeSource();
    this.currentProjectId = null;
    this.retryCount = 0;
    this.clearReconnect();
  }

  /** Whether the shared stream is currently open or connecting for a project. */
  isConnectedTo(projectId: string): boolean {
    return this.currentProjectId === projectId
      && this.eventSource !== null
      && (this.eventSource.readyState === 0 || this.eventSource.readyState === 1);
  }

  /** Whether the shared stream is fully open for a project (not connecting). */
  isOpenTo(projectId: string): boolean {
    return this.currentProjectId === projectId
      && this.eventSource !== null
      && this.eventSource.readyState === 1;
  }

  /** Whether this project owns the shared stream, including while disconnected. */
  isCurrentProject(projectId: string): boolean {
    return this.currentProjectId === projectId;
  }

  private reconnectCurrentProject(): void {
    const projectId = this.currentProjectId;

    if (!projectId || this.isConnectedTo(projectId)) return;
    this.clearReconnect();
    this.retryCount = 0;
    this.establishConnection(projectId);
  }

  private handleOnline(): void {
    this.reconnectCurrentProject();
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') this.reconnectCurrentProject();
  }

  /**
   * Subscribe to a specific event type.
   * Returns a cleanup function to unsubscribe.
   */
  subscribe(type: EventType, callback: EventCallback): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)?.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(type)?.delete(callback);
      if (this.listeners.get(type)?.size === 0) {
        this.listeners.delete(type);
      }
    };
  }

  /**
   * Emit a local event to all subscribers (no SSE needed).
   * Used for optimistic UI updates (e.g., cancel button clearing agents).
   */
  emit(type: EventType, data: Record<string, unknown>): void {
    this.notifyListeners(type, data);
  }

  // Event normalization intentionally stays centralized before fan-out.
  // eslint-disable-next-line max-statements
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      const type = data.type as EventType;

      if (type === 'heartbeat') return; // Ignore heartbeats

      // Bridge suggested_agents SSE → window.CustomEvent so useSuggestedAgents picks it up
      if (type === 'suggested_agents' && (data.agents || data.buttons)) {
        window.dispatchEvent(
          new globalThis.CustomEvent('suggested_agents', {
            detail: {
              agents: data.agents ?? [],
              buttons: data.buttons ?? [],
              projectId: data.project_id,
              messageId: data.message_id,
              sessionId: data.session_id ?? null,
              promptScope: data.prompt_scope ?? null,
              aiContent: data.ai_content ?? null,
            },
          }),
        );
      }
      if (type === 'chat_session_updated') {
        window.dispatchEvent(new globalThis.CustomEvent('chat_sessions_changed', { detail: data }));
      }

      this.notifyListeners(type, data);
    } catch (error) {
      console.error('[EventService] Error parsing event data:', error);
    }
  }

  private notifyListeners(type: EventType, data: unknown): void {
    const payload = data as Record<string, unknown>;

    if (this.listeners.has(type)) {
      this.listeners.get(type)?.forEach((callback) => {
        try {
          if (type === 'tasks_update') callback(payload.tasks);
          else if (type === 'requirements') callback(payload);
          else if (type === 'architecture') callback(payload);
          else if (type === 'agent_log') callback(payload);
          else if (type === 'sprints_update') callback(payload.sprints ?? payload);
          else if (type === 'build_phases_update') callback(payload.phases ?? payload);
          else callback(payload);
        } catch (err) {
          console.error(`[EventService] Error in listener for ${type}:`, err);
        }
      });
    }
  }

  /** Check if an SSE error is an auth failure (401/403). */
  private static isAuthError(error: unknown): boolean {
    const evt = error as { status?: number };

    return evt?.status === 401 || evt?.status === 403;
  }

  /**
   * On auth errors: attempt a single token refresh then reconnect.
   * On other errors: reconnect with exponential backoff up to the configured retry limit.
   */
  private handleAuthError(projectId: string): void {
    tokenService.refreshToken()
      .then(() => {
        if (this.currentProjectId === projectId) {
          this.retryCount = 0;
          this.establishConnection(projectId);
        }
      })
      .catch(() => {
        console.warn('[EventService] Token refresh failed, stopping reconnect');
        this.disconnect();
      });
  }

  private scheduleReconnect(projectId: string): void {
    const maxRetries = getClientTimeout('sseMaxRetries');

    this.retryCount += 1;

    if (this.retryCount > maxRetries) {
      console.warn(`[EventService] Max retries (${maxRetries}) reached, stopping`);

      return;
    }

    this.reconnectTimer = setTimeout(() => {
      if (this.currentProjectId === projectId) {
        this.establishConnection(projectId);
      }
    }, reconnectDelayMs(this.retryCount));
  }

  private handleError(error: unknown, projectId: string): void {
    this.closeSource();

    if (this.currentProjectId !== projectId) return;

    if (EventService.isAuthError(error)) {
      this.handleAuthError(projectId);
    } else {
      this.scheduleReconnect(projectId);
    }
  }
}

export const eventService = new EventService();
